import { lazy, Suspense, type ReactNode } from 'react';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { AuthProvider } from './auth/AuthProvider';
import { RequireAdmin } from './auth/RequireAdmin';
import { AppShell } from './components/AppShell';
import { Skeleton } from './components/Skeleton';
import { ToastProvider } from './components/toast/ToastProvider';

// Mevcut 4 ekran + LoginPage isimli (named) export kullanıyor -- React.lazy
// varsayılan export beklediği için .then() ile uyarlanır. Yeni STUB sayfalar
// zaten default export ediyor (bkz. features/*/*.tsx).
const LoginPage = lazy(() => import('./features/auth/LoginPage').then((m) => ({ default: m.LoginPage })));
const DashboardPage = lazy(() =>
  import('./features/dashboard/DashboardPage').then((m) => ({ default: m.DashboardPage })),
);
const UsersPage = lazy(() => import('./features/users/UsersPage').then((m) => ({ default: m.UsersPage })));
const DevicesPage = lazy(() => import('./features/devices/DevicesPage').then((m) => ({ default: m.DevicesPage })));
const ErrorsPage = lazy(() => import('./features/errors/ErrorsPage').then((m) => ({ default: m.ErrorsPage })));
const FeedbackPage = lazy(() => import('./features/feedback/FeedbackPage').then((m) => ({ default: m.FeedbackPage })));

const UserDetailPage = lazy(() => import('./features/users/UserDetailPage'));
const AnalyticsPage = lazy(() => import('./features/analytics/AnalyticsPage'));
const PremiumPage = lazy(() => import('./features/premium/PremiumPage'));
const PlaylistsPage = lazy(() => import('./features/playlists/PlaylistsPage'));
const AdminsPage = lazy(() => import('./features/admins/AdminsPage'));
const AuditLogPage = lazy(() => import('./features/auditLog/AuditLogPage'));
const SettingsPage = lazy(() => import('./features/settings/SettingsPage'));
const WebCastPage = lazy(() => import('./features/webcast/WebCastPage'));
const NotFoundPage = lazy(() => import('./features/notFound/NotFoundPage'));

function ProtectedShell({ children }: { children: ReactNode }) {
  return (
    <RequireAdmin>
      <AppShell>{children}</AppShell>
    </RequireAdmin>
  );
}

function SuspenseFallback() {
  return (
    <div className="centered-screen">
      <Skeleton width={160} height={16} />
    </div>
  );
}

export function App() {
  return (
    <AuthProvider>
      <ToastProvider>
        <BrowserRouter>
          <Suspense fallback={<SuspenseFallback />}>
            <Routes>
              <Route path="/login" element={<LoginPage />} />
              <Route
                path="/"
                element={
                  <ProtectedShell>
                    <DashboardPage />
                  </ProtectedShell>
                }
              />
              <Route
                path="/analytics"
                element={
                  <ProtectedShell>
                    <AnalyticsPage />
                  </ProtectedShell>
                }
              />
              <Route
                path="/users"
                element={
                  <ProtectedShell>
                    <UsersPage />
                  </ProtectedShell>
                }
              />
              <Route
                path="/users/:id"
                element={
                  <ProtectedShell>
                    <UserDetailPage />
                  </ProtectedShell>
                }
              />
              <Route
                path="/premium"
                element={
                  <ProtectedShell>
                    <PremiumPage />
                  </ProtectedShell>
                }
              />
              <Route
                path="/playlists"
                element={
                  <ProtectedShell>
                    <PlaylistsPage />
                  </ProtectedShell>
                }
              />
              <Route
                path="/devices"
                element={
                  <ProtectedShell>
                    <DevicesPage />
                  </ProtectedShell>
                }
              />
              <Route
                path="/errors"
                element={
                  <ProtectedShell>
                    <ErrorsPage />
                  </ProtectedShell>
                }
              />
              <Route
                path="/feedback"
                element={
                  <ProtectedShell>
                    <FeedbackPage />
                  </ProtectedShell>
                }
              />
              <Route
                path="/web-cast"
                element={
                  <ProtectedShell>
                    <WebCastPage />
                  </ProtectedShell>
                }
              />
              <Route
                path="/admins"
                element={
                  <ProtectedShell>
                    <AdminsPage />
                  </ProtectedShell>
                }
              />
              <Route
                path="/audit-log"
                element={
                  <ProtectedShell>
                    <AuditLogPage />
                  </ProtectedShell>
                }
              />
              <Route
                path="/settings"
                element={
                  <ProtectedShell>
                    <SettingsPage />
                  </ProtectedShell>
                }
              />
              <Route
                path="*"
                element={
                  <ProtectedShell>
                    <NotFoundPage />
                  </ProtectedShell>
                }
              />
            </Routes>
          </Suspense>
        </BrowserRouter>
      </ToastProvider>
    </AuthProvider>
  );
}
